import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import {
  AdvertiserShareOfVoiceDTO,
  API,
  CompanyShareOfVoiceDTO,
  CullDTO,
  MediumGroupCategoryShareOfVoiceDTO,
  MediumGroupShareOfVoiceDTO,
  MediumShareOfVoiceDTO,
  PlayerAggregationDataDTO,
  PlayoutAggregationDataDateEntryDTO,
  PublicMediumDTO,
} from '../../../../services/api/api';
import { QueryParamService } from '../../../../services/lists/query-params';
import { SbDateTime, SbLocalDate } from '../../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { UtilService } from '../../../../services/util.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, take } from 'rxjs/operators';
import { ErrorService } from '../../../../services/error.service';
import { ChartComponent, PieChartModel, XYChartModel } from '@ngx-seatback/charts';
import { TranslateService } from '@ngx-translate/core';
import { MsOptions, ToggleValue } from '../../../../services/global/global-interfaces';
import { PermissionService } from '../../../../services/permission.service';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';

interface AdvertiserSoVTree {
  internalId: number;
  id: number;
  name: string;
  type: string;
  color?: string;
  medium?: PublicMediumDTO;
  secondLine?: string;
  thirdLine?: string;
  playoutsHour?: PlayoutAggregationDataDateEntryDTO[];
  playouts: number;
  percentage: string;
  overallPercentage: string;
  playerAggregation: PlayerAggregationDataDTO;
  subTree?: AdvertiserSoVTree[];
}

interface ChartAdvertiserInfo {
  id?: number | null;
  name: string;
  playouts?: number;
  data: AdvertiserSoVTree[] | null;
}

export const displayableTypesSoV = ['Count', 'Time', 'Audience'] as const;
export type DisplayTypeSoV = Exclude<(typeof displayableTypesSoV)[number], 'Audience'>;

@Component({
  selector: 'sb-page-advertiser-share-of-voice',
  templateUrl: './advertiser-share-of-voice.component.html',
  styleUrl: './advertiser-share-of-voice.component.scss',
  standalone: false,
})
export class AdvertiserShareOfVoiceComponent implements OnInit {
  private readonly stringTransformation = inject(StringTransformationService);

  protected readonly Number = Number;
  @ViewChild('pieChart') pieChart: ChartComponent;
  @ViewChild('lineChart') lineChart: ChartComponent;

  productiveToggle: ToggleValue;
  queryParamsParsed: boolean;
  debounceTimer: ReturnType<typeof setTimeout>;
  loading: boolean;

  overallPlayouts: number;
  overallPlayoutTime: number;

  msPlayerOptions: MsOptions;
  filteredCull: CullDTO;
  playerCulls: CullDTO[];

  defaultColorPalette: string[] = [
    '#76D88F',
    '#FFD168',
    '#FF8982',
    '#9AC1F5',
    '#DADDE2',
    '#876EA8',
    '#D6D59F',
    '#AED8E4',
    '#FFEE93',
    '#714469',
    '#A6BBC5',
    '#C29C42',
    '#607FA9',
    '#6DB77F',
    '#CAB5C8',
    '#16A7A2',
    '#5A764F',
    '#BD8787',
    '#CBD3E8',
    '#E25353',
    '#F7C4A5',
    '#658199',
    '#5EBCD8',
    '#A19AC3',
    '#D77E92',
    '#6F5F76',
    '#1299B7',
    '#036474',
    '#E7B3A6',
    '#5A777C',
    '#94ACD3',
    '#6F2978',
  ];
  pmgColor: Map<string, string> = new Map([
    ['CC', '#de1414'],
    ['P1', '#00d1d2'],
    ['P4', '#f38a00'],
  ]);

  _from?: SbLocalDate;
  _till?: SbLocalDate;

  selectedDataset: ChartAdvertiserInfo;

  // companyDisplay: advertiserSoVMap;
  companies: AdvertiserSoVTree[] | null;
  pieChartData: PieChartModel = {
    title: 'Playouts',
    height: 200,
    width: 100,
    value: 'value',
    category: 'category',
    fontSize: 23,
    radius: 60,
    sumLabel: false,
    customLabel: '',
    data: [],
  };
  hourData: XYChartModel = {
    data: [],
    height: 200,
    width: 100,
    loadingDone: false,
    title: '',
    valueAssignments: [],
    categoryType: 'date',
    tensionX: 1,
    legendAlign: 'right',
    showTotalInTooltip: true,
    legendContainerWidth: '600px',
  };
  threshold: number;
  selectedDisplayType: DisplayTypeSoV;
  displayableTypesSoV = displayableTypesSoV.slice(0, 2);

  private report: AdvertiserShareOfVoiceDTO;
  showSubTree: Map<number, boolean>;
  noDataFound: boolean;
  isAllExpanded: boolean;
  private internalID = 0;

  get from(): SbLocalDate {
    return this._from;
  }

  set from(val) {
    this._from = val;
  }

  get till(): SbLocalDate {
    return this._till;
  }

  set till(val) {
    this._till = val;
  }

  constructor(
    private readonly api: API,
    private readonly queryParamService: QueryParamService,
    public readonly util: UtilService,
    private readonly destroyRef: DestroyRef,
    private readonly errorHandler: ErrorService,
    private readonly translate: TranslateService,
    public readonly permissionService: PermissionService
  ) {
    this.productiveToggle = 1;
    this.selectedDisplayType = 'Count';
    this.isAllExpanded = false;
    this.noDataFound = null;
    this.showSubTree = new Map();
    this.report = null;
    this.threshold = 0.1;
    this.companies = null;
    this.debounceTimer = null;
    this.loading = true;
    this.filteredCull = null;
    this.playerCulls = [];
    this.from = new SbLocalDate(DateTime.local().startOf('day').minus({ day: 7 }));
    this.till = new SbLocalDate(DateTime.local().startOf('day').minus({ day: 1 }));
  }

  ngOnInit() {
    this.parseQueryParams();
  }

  parseQueryParams() {
    this.parseDateParams();
  }

  parseDateParams() {
    if (this.queryParamService.getQueryParam('from') && this.queryParamService.getQueryParam('till')) {
      this.from = new SbLocalDate(DateTime.fromISO(this.queryParamService.getQueryParam('from')));
      this.till = new SbLocalDate(DateTime.fromISO(this.queryParamService.getQueryParam('till')));
    }
  }

  refreshQueryParams() {
    if (this.from) {
      this.queryParamService.setQueryParam('from', this.from.toString());
    } else {
      this.queryParamService.clearQueryParam('from');
    }
    if (this.till) {
      this.queryParamService.setQueryParam('till', this.till.toString());
    } else {
      this.queryParamService.clearQueryParam('till');
    }
  }

  requestDebounced() {
    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.loading = true;
    this.noDataFound = false;
    this.debounceTimer = setTimeout(() => {
      this.refreshFromServer();
    }, 500);
  }

  refreshFromServer() {
    this.report = null;
    const requestOptions = {
      from: this.from ? this.from : null,
      till: this.till ? this.till : null,
      playerCull: this.filteredCull?.id ?? null,
      productive: this.productiveToggle === -1 ? null : Boolean(this.productiveToggle),
    };
    this.api
      .ds_cm_Reporting_retrieveShareOfVoice(requestOptions)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (report: AdvertiserShareOfVoiceDTO) => {
          this.report = report;
          if (this.report?.companies?.length > 0) {
            this.noDataFound = false;
            this.companies = this.transformToCompaniesDisplay(report);
            // chart initialization needs to be called after displayObject initialized
            this.selectDataset();
            if (this.isAllExpanded) {
              this.expandTree();
            }
          } else {
            this.noDataFound = true;
          }
        },
        error: error => {
          this.noDataFound = true;
          if (error.error.status === 400 && error.error.message === 'no campaign report data found') {
            this.companies = [];
            this.loading = false;
          } else {
            this.loading = false;
            this.errorHandler.errorHandler(error);
          }
        },
      });
  }

  generateId(): number {
    return this.internalID++;
  }

  transformToCompaniesDisplay(reporting: AdvertiserShareOfVoiceDTO): AdvertiserSoVTree[] | null {
    if (!reporting) {
      return;
    }
    this.internalID = 0; // reset internal id
    this.overallPlayouts = reporting.data.hourCount.reduce((a, b) => a + b, 0);
    this.overallPlayoutTime = reporting.data.hourTime.reduce((a, b) => a + b, 0);
    return reporting.companies
      .map((company, index) => this.transformCompany(company, index))
      .filter((node): node is AdvertiserSoVTree => node !== null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  private transformCompany(company: CompanyShareOfVoiceDTO, companyIndex: number): AdvertiserSoVTree | null {
    const playoutsForCompanyDates = company.dates;
    const playouts: Record<DisplayTypeSoV, number> = {
      Count: company.data.hourCount.reduce((a, b) => a + b, 0),
      Time: company.data.hourTime.reduce((a, b) => a + b, 0),
    };
    const percentages: Record<DisplayTypeSoV, number> = {
      Count: this.calculatePercentage(playouts.Count, this.overallPlayouts),
      Time: this.calculatePercentage(playouts.Time, this.overallPlayoutTime),
    };
    if (percentages[this.selectedDisplayType] < this.threshold) {
      return null;
    }
    // percentage is equal or greater than threshold -> calculate subtree

    const children: AdvertiserSoVTree[] = [];
    if (company.mediumGroupCategories) {
      const categories = company.mediumGroupCategories
        .map(mediumGroupCategory => this.transformMediumGroupCategory(mediumGroupCategory, playouts))
        .filter((node): node is AdvertiserSoVTree => node !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      children.push(...categories);
    }
    const color = this.pmgColor.get(company.company?.name);
    const companyColor = color ?? this.defaultColorPalette[companyIndex % this.defaultColorPalette.length];
    return {
      internalId: this.generateId(),
      id: company.company?.id,
      name: company.company?.name,
      type: this.translate.instant('lang.globals.company'),
      color: companyColor,
      playoutsHour: playoutsForCompanyDates,
      playouts: playouts[this.selectedDisplayType],
      playerAggregation: company.playerAggregation,
      percentage: percentages[this.selectedDisplayType].toFixed(2),
      overallPercentage: this.calculatePercentage(playouts[this.selectedDisplayType], this.overallPlayouts).toFixed(2),
      subTree: children.length > 0 ? children : null,
    };
  }

  private transformMediumGroupCategory(
    category: MediumGroupCategoryShareOfVoiceDTO,
    playoutsForSubtree: Record<DisplayTypeSoV, number>
  ): AdvertiserSoVTree | null {
    const playouts: Record<DisplayTypeSoV, number> = {
      Count: category.data.hourCount.reduce((a, b) => a + b, 0),
      Time: category.data.hourTime.reduce((a, b) => a + b, 0),
    };
    const percentages: Record<DisplayTypeSoV, number> = {
      Count: this.calculatePercentage(playouts.Count, this.overallPlayouts),
      Time: this.calculatePercentage(playouts.Time, this.overallPlayoutTime),
    };
    if (percentages[this.selectedDisplayType] < this.threshold) {
      return null;
    }
    // percentage is equal or greater than threshold -> calculate subtree

    const children: AdvertiserSoVTree[] = [];

    if (category.subCategories) {
      const categories = category.subCategories
        .map(mediumGroupCategory => this.transformMediumGroupCategory(mediumGroupCategory, playouts))
        .filter((node): node is AdvertiserSoVTree => node !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      children.push(...categories);
    }

    if (category.mediumGroups) {
      const groups = category.mediumGroups
        .map(mediumGroup => this.transformMediumGroup(mediumGroup, playouts))
        .filter((node): node is AdvertiserSoVTree => node !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      children.push(...groups);
    }

    const percentageForCategory: number =
      playoutsForSubtree[this.selectedDisplayType] !== 0
        ? (playouts[this.selectedDisplayType] / playoutsForSubtree[this.selectedDisplayType]) * 100
        : 0;

    return {
      internalId: this.generateId(),
      name: category.mediumGroupCategory?.name ?? this.translate.instant('lang.globals.no.category'),
      id: category.mediumGroupCategory?.id,
      type: this.translate.instant('lang.globals.category'),
      playoutsHour: category.dates,
      playouts: playouts[this.selectedDisplayType],
      playerAggregation: category.playerAggregation,
      percentage: percentageForCategory.toFixed(2),
      overallPercentage: percentages[this.selectedDisplayType].toFixed(2),
      subTree: children.length > 0 ? children : null,
    };
  }

  private transformMediumGroup(
    mediumGroup: MediumGroupShareOfVoiceDTO,
    playoutsForSubtree: Record<DisplayTypeSoV, number>
  ): AdvertiserSoVTree | null {
    const playouts: Record<DisplayTypeSoV, number> = {
      Count: mediumGroup.data.hourCount.reduce((a, b) => a + b, 0),
      Time: mediumGroup.data.hourTime.reduce((a, b) => a + b, 0),
    };
    const percentages: Record<DisplayTypeSoV, number> = {
      Count: this.calculatePercentage(playouts.Count, this.overallPlayouts),
      Time: this.calculatePercentage(playouts.Time, this.overallPlayoutTime),
    };
    if (percentages[this.selectedDisplayType] < this.threshold) {
      return null;
    }
    // percentage is equal or greater than threshold -> calculate subtree

    const children: AdvertiserSoVTree[] = [];
    if (mediumGroup.media) {
      const media = mediumGroup.media
        .map(groupMedium => this.transformMedia(groupMedium, playouts))
        .filter((node): node is AdvertiserSoVTree => node !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      children.push(...media);
    }

    const percentageForMediumGroup: number =
      playoutsForSubtree[this.selectedDisplayType] !== 0
        ? (playouts[this.selectedDisplayType] / playoutsForSubtree[this.selectedDisplayType]) * 100
        : 0;

    return {
      internalId: this.generateId(),
      name: mediumGroup.mediumGroup?.name ?? this.translate.instant('lang.globals.no.mediumGroup'),
      id: mediumGroup.mediumGroup?.id,
      medium: mediumGroup.mediumGroup?.preview,
      secondLine:
        mediumGroup.media?.length.toString() +
        ' / ' +
        (mediumGroup.mediumGroup?.mediaCount ?? 0).toString() +
        ' ' +
        this.translate.instant('lang.globals.motives'),
      type: this.translate.instant('lang.globals.mediumGroup'),
      playoutsHour: mediumGroup.dates,
      playouts: playouts[this.selectedDisplayType],
      playerAggregation: mediumGroup.playerAggregation,
      percentage: percentageForMediumGroup.toFixed(2),
      overallPercentage: percentages[this.selectedDisplayType].toFixed(2),
      subTree: children.length > 0 ? children : null,
    };
  }

  private transformMedia(medium: MediumShareOfVoiceDTO, playoutsForSubtree: Record<DisplayTypeSoV, number>): AdvertiserSoVTree | null {
    if (!medium) {
      return null;
    }
    const playouts: Record<DisplayTypeSoV, number> = {
      Count: medium.data.hourCount.reduce((a, b) => a + b, 0),
      Time: medium.data.hourTime.reduce((a, b) => a + b, 0),
    };
    const percentages: Record<DisplayTypeSoV, number> = {
      Count: this.calculatePercentage(playouts.Count, this.overallPlayouts),
      Time: this.calculatePercentage(playouts.Time, this.overallPlayoutTime),
    };
    if (percentages[this.selectedDisplayType] < this.threshold) {
      return null;
    }
    // percentage is equal or greater than threshold -> calculate subtree

    const children: AdvertiserSoVTree[] = [];
    if (medium.media) {
      const media = medium.media
        .map(pMedium => this.transformMedia(pMedium, playouts))
        .filter((node): node is AdvertiserSoVTree => node !== null)
        .sort((a, b) => a.name.localeCompare(b.name));
      children.push(...media);
    }

    const percentageForMediumGroup: number =
      playoutsForSubtree[this.selectedDisplayType] !== 0
        ? (playouts[this.selectedDisplayType] / playoutsForSubtree[this.selectedDisplayType]) * 100
        : 0;

    return {
      internalId: this.generateId(),
      name: medium.medium?.name,
      id: medium.medium?.id,
      medium: medium.medium,
      secondLine: this.translate.instant('lang.globals.resolution') + ': ' + medium.medium?.resolution?.id,
      thirdLine: this.translate.instant('lang.globals.playoutDuration') + ': ' + this.buildSecondLineOfMedium(medium, playouts),
      type: this.translate.instant('lang.globals.motives'),
      playoutsHour: medium.dates,
      playouts: playouts[this.selectedDisplayType],
      playerAggregation: medium.playerAggregation,
      percentage: percentageForMediumGroup.toFixed(2),
      overallPercentage: percentages[this.selectedDisplayType].toFixed(2),
      subTree: children.length > 0 ? children : null,
    };
  }

  private calculatePercentage(currentValue: number, overallValue: number) {
    if (!overallValue) {
      throw new Error('You cannot divide by 0');
    }
    if (!currentValue) {
      return 0;
    }
    return (currentValue / overallValue) * 100;
  }

  private buildSecondLineOfMedium(medium: MediumShareOfVoiceDTO, playouts: Record<DisplayTypeSoV, number>): string {
    let res = '';
    res += (playouts.Time / playouts.Count).toFixed(2) + 's';
    if (medium.medium?.duration) {
      res += ' (' + this.translate.instant('lang.globals.mediumDuration') + ': ' + medium.medium?.duration + 's)';
    }
    return res;
  }

  /*
  prepareCompanyDisplay(reporting: AdvertiserShareOfVoiceDTO) {
    this.companyDisplay = new Map();
    if (!reporting) {
      return;
    }
    let internalID: number = 0;
    const overallPlayouts = reporting.data.hourCount.reduce((a, b) => a + b, 0);
    this.overallPlayouts = overallPlayouts;
    for (const [companyIndex, company] of reporting.companies.entries()) {
      const playoutsForCompanyDates: any[] = company.dates;
      const playoutsForCompany: number = company.data.hourCount.reduce((a, b) => a + b, 0);
      const percentageForCompany: number = playoutsForCompany !== 0 ? (playoutsForCompany / overallPlayouts) * 100 : 0;
      if (percentageForCompany > this.threshold) {
        const valuesForCategory: advertiserSoVMap = new Map();

        company.mediumGroupCategories.forEach(category => {
          const playoutsForCategory: number = category.data.hourCount.reduce((a, b) => a + b, 0);
          const playoutsForCategoryDates: any[] = category.dates;
          const percentageForCategory: number = playoutsForCompany !== 0 ? (playoutsForCategory / playoutsForCompany) * 100 : 0;
          const overallPercentageForCategory: number = overallPlayouts !== 0 ? (playoutsForCategory / overallPlayouts) * 100 : 0;

          if (percentageForCategory > this.threshold) {
            const valuesForMediumGroup: advertiserSoVMap = new Map();
            category.mediumGroups.forEach(group => {
              const playoutsForMediumGroup: number = group.data.hourCount.reduce((a, b) => a + b, 0);
              const playoutsForMediumGroupDates: any[] = group.dates;
              const percentageForMediumGroup: number = playoutsForCategory !== 0 ? (playoutsForMediumGroup / playoutsForCategory) * 100 : 0;
              const overallPercentageForMediumGroup: number = overallPlayouts !== 0 ? (playoutsForMediumGroup / overallPlayouts) * 100 : 0;

              if (percentageForMediumGroup > this.threshold) {
                const valuesForMedia: advertiserSoVMap = new Map();
                group.media.forEach(medium => {
                  const playoutsForMedia: number = medium.data.hourCount.reduce((a, b) => a + b, 0);
                  const playoutsForMediaDates: any[] = medium.dates;
                  const percentageForMedia: number = playoutsForMediumGroup !== 0 ? (playoutsForMedia / playoutsForMediumGroup) * 100 : 0;
                  const overallPercentageForMedia: number = overallPlayouts !== 0 ? (playoutsForMedia / overallPlayouts) * 100 : 0;

                  if (percentageForMedia > this.threshold) {
                    valuesForMedia.set(internalID, {
                      name: medium?.medium?.name,
                      id: medium?.medium?.id,
                      medium: medium.medium,
                      subline: medium.medium?.duration ? medium.medium?.duration + 's' : '',
                      type: this.translate.instant('lang.globals.motives'),
                      playoutsHour: playoutsForMediaDates,
                      playouts: playoutsForMedia,
                      percentage: percentageForMedia.toFixed(2),
                      overallPercentage: overallPercentageForMedia.toFixed(2),
                    });
                    internalID++;
                  }
                });
                valuesForMediumGroup.set(internalID, {
                  name: group.mediumGroup?.name ?? this.translate.instant('lang.globals.no.mediumGroup'),
                  id: group.mediumGroup?.id,
                  medium: group.mediumGroup?.preview,
                  subline: group.mediumGroup?.mediaCount
                    ? group.mediumGroup?.mediaCount + ' ' + this.translate.instant('lang.globals.motives')
                    : '',
                  type: this.translate.instant('lang.globals.mediumGroup'),
                  playoutsHour: playoutsForMediumGroupDates,
                  playouts: playoutsForMediumGroup,
                  percentage: percentageForMediumGroup.toFixed(2),
                  overallPercentage: overallPercentageForMediumGroup.toFixed(2),
                  subTree: valuesForMedia,
                });
                internalID++;
              }
            });

            valuesForCategory.set(internalID, {
              name: category.mediumGroupCategory?.name ?? this.translate.instant('lang.globals.no.category'),
              id: category.mediumGroupCategory?.id,
              type: this.translate.instant('lang.globals.category'),
              playoutsHour: playoutsForCategoryDates,
              playouts: playoutsForCategory,
              percentage: percentageForCategory.toFixed(2),
              overallPercentage: overallPercentageForCategory.toFixed(2),
              subTree: valuesForMediumGroup,
            });
            internalID++;
          }
        });
        const color = this.pmgColor.get(company.company?.name);
        const companyColor = color ?? this.defaultColorPalette[companyIndex % this.defaultColorPalette.length];

        this.companyDisplay.set(internalID, {
          id: company.company?.id,
          name: company.company?.name,
          type: this.translate.instant('lang.globals.company'),
          color: companyColor,
          playoutsHour: playoutsForCompanyDates,
          playouts: playoutsForCompany,
          percentage: overallPlayouts !== 0 ? ((playoutsForCompany / overallPlayouts) * 100).toFixed(2) : '0',
          overallPercentage: overallPlayouts !== 0 ? ((playoutsForCompany / overallPlayouts) * 100).toFixed(2) : '0',
          subTree: valuesForCategory,
        });
        internalID++;
      }
    }
  }
*/
  selectDataset(item?: AdvertiserSoVTree) {
    if (item == null) {
      this.selectedDataset = {
        name: this.translate.instant('lang.globals.overview'),
        data: this.companies,
      };
    } else {
      this.selectedDataset = {
        id: item.internalId,
        name: item.name,
        playouts: item.playouts,
        data: item.subTree,
      };
    }
    this.prepareLineChartData();
    this.preparePieChartData();
  }

  preparePieChartData() {
    this.pieChartData.data = [];
    this.selectedDataset.data.forEach(value => {
      this.pieChartData.data.push({
        category: value.name,
        value: value.playouts,
        color: value.color,
      });
    });
    const playouts = this.selectedDataset.playouts ?? this.overallPlayouts;
    this.pieChartData.customLabel = this.stringTransformation.truncateNumber(playouts);
    if (this.pieChart) {
      this.pieChart.rerenderChart(this.pieChartData);
    }
  }

  prepareLineChartData() {
    this.hourData.data = [];
    this.hourData.valueAssignments = [];
    this.selectedDataset.data.forEach(value => {
      this.hourData.valueAssignments.push({
        value: value.name,
        valueName: value.name,
        color: value.color,
        categories: ['date'],
      });
      value.playoutsHour.forEach(val => {
        const calculationData = this.selectedDisplayType === 'Count' ? val.data.hourCount : val.data.hourTime;
        this.hourData.data.push({
          date: val.date,
          [value.name]: calculationData.reduce((accumulator, currentValue) => {
            return accumulator + currentValue;
          }, 0),
        });
      });
    });
    if (this.lineChart) {
      this.lineChart.rerenderChart(this.hourData);
    }
  }

  handleDateChange($event: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if ($event?.fromDate && $event?.toDate) {
      this.from = new SbLocalDate($event.fromDate.value);
      this.till = new SbLocalDate($event.toDate.value);
    }
  }

  adjustThreshold($event: number) {
    this.threshold = $event;
    this.companies = this.transformToCompaniesDisplay(this.report);
  }

  toggleShowChildren(key: number, value: AdvertiserSoVTree) {
    if (this.showSubTree.has(key)) {
      if (value.type !== this.translate.instant('lang.globals.mediumGroup')) {
        this.isAllExpanded = false;
      }
      this.removeVisibility(key, value);
    } else {
      this.showSubTree.set(key, true);
    }
  }

  private removeVisibility(key: number, value: AdvertiserSoVTree) {
    this.showSubTree.delete(key);
    const traversedChildren = this.traverseChildren(value, []);
    traversedChildren?.forEach(child => this.showSubTree.delete(child));
  }

  traverseChildren(value: AdvertiserSoVTree, result: number[], toggleAll?: boolean): number[] {
    if (!value.subTree || (toggleAll && value.type === this.translate.instant('lang.globals.category'))) {
      return result;
    }
    result.push(...value.subTree.map(tree => tree.internalId));
    for (const subTreeValue of value.subTree) {
      this.traverseChildren(subTreeValue, result, toggleAll);
    }
    return result;
  }

  update($event: CullDTO[], type: string) {
    if (type === 'PLAYER_CULLS') {
      if ($event && $event.length > 0) {
        this.filteredCull = $event[0];
      } else {
        this.filteredCull = null;
      }
    }
  }

  msRequest(type: string, options?: MsOptions) {
    if (type === 'PLAYER_CULLS') {
      if (options) {
        this.msPlayerOptions = options;
      }
      if (this.msPlayerOptions?.pagination) {
        this.api
          .cs_Cull_retrieveAll({
            search: this.stringTransformation.encodeSearchQueryParam(false, this.msPlayerOptions?.search),
            type: 'PlayerCull',
          })
          .subscribe(culls => {
            this.playerCulls = culls;
          });
      }
    }
  }

  toggleExpandAll() {
    if (this.isAllExpanded) {
      this.resetExpandTree();
    } else {
      this.isAllExpanded = true;
      this.expandTree();
    }
  }

  private resetExpandTree() {
    this.isAllExpanded = false;
    this.showSubTree = new Map<number, boolean>();
  }

  private expandTree() {
    this.companies.forEach(value => {
      this.showSubTree.set(value.internalId, true);
      this.traverseChildren(value, [], true).forEach(childKey => {
        this.showSubTree.set(childKey, true);
      });
    });
  }

  identityType(data: any) {
    return data as AdvertiserSoVTree[];
  }

  adjustDisplayType($event: DisplayTypeSoV) {
    this.selectedDisplayType = $event;
    this.resetExpandTree();
    this.companies = this.transformToCompaniesDisplay(this.report);
    this.selectDataset();
  }
}
